import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { CITY_CONFIG, CITY_ORDER, DEFAULT_GEO_FALLBACK_CITY } from '../config/cities';
import { getAPIRoot } from '../services/api';
import { getUserCoordsOnce, GEO_PERMISSION_DENIED } from '../utils/userGeolocation';
import { pickNearestLiveCity } from '../utils/geo';
import { useToast } from '../components/shared/ToastNotification';

const CityContext = createContext();

const LS = {
  selected: 'fridaystag_selected_city',
  userPicked: 'fridaystag_city_user_picked',
  geoDone: 'fridaystag_city_geo_inferred',
};

function readCityAtPageLoad() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { has: false, city: null };
  }
  try {
    const s = localStorage.getItem(LS.selected);
    if (s && CITY_CONFIG[s]) return { has: true, city: s };
  } catch (_) {}
  return { has: false, city: null };
}

const HAD_CITY_AT_PAGELOAD = readCityAtPageLoad();

const defaultCityIfUnknown =
  CITY_ORDER.find((city) => CITY_CONFIG[city]?.work || CITY_CONFIG[city]?.stag) || CITY_ORDER[0] || DEFAULT_GEO_FALLBACK_CITY;

export const useCity = () => {
  const context = useContext(CityContext);
  if (!context) {
    throw new Error('useCity must be used within a CityProvider');
  }
  return context;
};

export const CityProvider = ({ children }) => {
  const { showInfo, showSuccess } = useToast();

  const [cityOptions, setCityOptions] = useState(() =>
    CITY_ORDER.map((name, index) => ({
      name,
      slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      sort_order: (index + 1) * 10,
      is_active: true,
      stag_enabled: Boolean(CITY_CONFIG[name]?.stag),
      work_enabled: Boolean(CITY_CONFIG[name]?.work),
      tagline: CITY_CONFIG[name]?.tagline || '',
      accent: CITY_CONFIG[name]?.accent || '',
      emoji: CITY_CONFIG[name]?.emoji || '',
      timezone: 'Asia/Kolkata',
    })),
  );

  const mounted = useRef(true);
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const [selectedCity, setSelectedCityInternal] = useState(() => {
    if (typeof window === 'undefined') return defaultCityIfUnknown;
    if (HAD_CITY_AT_PAGELOAD.has) return HAD_CITY_AT_PAGELOAD.city;
    return defaultCityIfUnknown;
  });

  const setSelectedCity = useCallback((value) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(LS.userPicked, '1');
      } catch (_) {}
    }
    setSelectedCityInternal(value);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(LS.selected, selectedCity);
    } catch (_) {}
  }, [selectedCity]);

  // One-time GPS → city for first-time / no prior saved city at page load; respects manual picks and returning users.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (localStorage.getItem(LS.userPicked) === '1') {
      if (localStorage.getItem(LS.geoDone) !== '1') {
        try {
          localStorage.setItem(LS.geoDone, '1');
        } catch (_) {}
      }
      return;
    }
    if (HAD_CITY_AT_PAGELOAD.has) {
      try {
        localStorage.setItem(LS.geoDone, '1');
      } catch (_) {}
      return;
    }
    if (localStorage.getItem(LS.geoDone) === '1') return;

    try {
      localStorage.setItem(LS.geoDone, '1');
    } catch (_) {}

    getUserCoordsOnce().then((res) => {
      if (!mounted.current) return;
      if (!res.ok) {
        if (res.code === GEO_PERMISSION_DENIED) {
          showInfo('Location access was denied. Pick your city from the city / location control in the header.', {
            title: 'City',
            duration: 4200,
          });
        } else {
          showInfo('We couldn’t detect your location. Choose your city from the city / location menu in the header.', {
            title: 'Location',
            duration: 4200,
          });
        }
        return;
      }
      const r = pickNearestLiveCity(res.lat, res.lng);
      if (r.city && CITY_CONFIG[r.city]) {
        setSelectedCityInternal(r.city);
      }
      if (r.isFallback) {
        showInfo(
          `You’re outside our live market range — showing ${r.city}. Change it anytime from the header.`,
          { title: 'City', duration: 4200 },
        );
      } else {
        showSuccess(`Viewing ${r.city} based on your location.`, { title: 'City', duration: 3200 });
      }
    });
  }, [showInfo, showSuccess]);

  // Merge server /cities into options; fix selected if the API no longer includes it (runs once on mount).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getAPIRoot()}/cities`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const rows = Array.isArray(data?.cities) ? data.cities : [];
        if (cancelled || rows.length === 0) return;
        setCityOptions(rows);
        setSelectedCityInternal((prev) => {
          if (!rows.some((city) => city.name === prev)) {
            const firstLive = rows.find((city) => city.stag_enabled || city.work_enabled) || rows[0];
            if (firstLive?.name) return firstLive.name;
          }
          return prev;
        });
      } catch (_) {
        // Static config stays as the offline fallback.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = {
    selectedCity,
    setSelectedCity,
    cityOptions,
  };

  return <CityContext.Provider value={value}>{children}</CityContext.Provider>;
};
